import { Divider, Space, Typography } from 'antd'
import { QRCodeSVG }                         from 'qrcode.react'
import { useIntl } from 'react-intl';
import {PassphraseViewer}                    from '../components/password/PassphraseViewer';
import {escapeSpecialCharacters} from './utils';
import { ErrorContent } from '../components/error';
import { useMemo } from 'react';

interface PassphraseOnboardedVariables {
  IDENTITY_ID: string;
  IDENTITY_GROUP_ID: string;
  NETWORK: string;
  PASSPHRASE: string;
  SAE_PASSPHRASE?: string;
  COMMUNICATION_AUTO_DELIVERY?: string;
  USER_EMAIL_ENABLED?: string;
  USER_SMS_ENABLED?: string;
  WIFI_QRCODE?: string;
  WIFI_SAE_QRCODE?: string;
}

export function PassphraseOnboarded (props: { data: PassphraseOnboardedVariables }) {
  const { data } = props
  const { $t } = useIntl()
  const { Title, Text } = Typography

  const {standardQRCode, mixedModeSAEQRCode} = useMemo(() => {
    let standardQRCode = ''
    if(data.WIFI_QRCODE) {
      standardQRCode = data.WIFI_QRCODE + '\0';;
    } else if(data.PASSPHRASE) {
      // NOTE: this is for backwards compatibility in case we don't have the QRCode value from the enrollment variables
      // remove this once the enrollment variables always include the QRCode info
      standardQRCode = `WIFI:T:WPA;S:${data.NETWORK};P:${escapeSpecialCharacters(data.PASSPHRASE)};H:false;`
    }

    let mixedModeSAEQRCode = '';
    if(data.WIFI_SAE_QRCODE) {
      mixedModeSAEQRCode = data.WIFI_SAE_QRCODE + '\0';
    } else if(data.SAE_PASSPHRASE) {
      // NOTE: this is for backwards compatibility in case we don't have the QRCode value from the enrollment variables
      // remove this once the enrollment variables always include the QRCode info
      mixedModeSAEQRCode = `WIFI:T:WPA;S:${data.NETWORK};P:${escapeSpecialCharacters(data.SAE_PASSPHRASE)};H:false;`
    }

    return {standardQRCode, mixedModeSAEQRCode}

  }, [data])

  if (!data.NETWORK || data.NETWORK === '') {
    return (<ErrorContent message={$t({ defaultMessage: 'No networks available.' })}/>)
  }

  return (
      <Space direction='vertical' size='large'>
        <Title level={2}>
          {$t({defaultMessage: 'Connect to the network:'})}
        </Title>
        <Space>
          <Text strong>
            {$t({defaultMessage: 'Wi-Fi Network:'})}
          </Text>
          <Text>{ data.NETWORK }</Text>
        </Space>
        {data.SAE_PASSPHRASE && (<>
          <Space direction='vertical' size={0} style={{ width: '100%' }}>
            <Space data-testid='sae-passphrase-onboarded-block'>
              <Text strong>
                {$t({defaultMessage: 'WPA3 Password:'})}
              </Text>
              <PassphraseViewer
                passphrase={data.SAE_PASSPHRASE}
                emailNotification={data.COMMUNICATION_AUTO_DELIVERY === 'false'
                  && data.USER_EMAIL_ENABLED === 'true'}
                smsNotification={data.COMMUNICATION_AUTO_DELIVERY === 'false'
                  && data.USER_SMS_ENABLED === 'true'}
              />
            </Space>
            <Text style={{fontSize: '12px', lineHeight: '16px', color: '#565758'}}>
              {$t({defaultMessage: '[Recommended] Use this for modern devices (2019 and later)'})}
            </Text>
          </Space>
          <Space direction='vertical' align='center' style={{width: '100%'}}>
            <Text>
              {$t({defaultMessage: 'Scan this QR code to connect to the network with WPA3:'})}
            </Text>
            <QRCodeSVG
              data-testid='qr-code'
              value={mixedModeSAEQRCode}
              size={180}
              bgColor={'#ffffff'}
              fgColor={'#000000'}
              level={'L'}
              includeMargin={false}
            />
          </Space>
        </>)}
        {data.SAE_PASSPHRASE && data.PASSPHRASE && (
          <Divider style={{ margin: '16px 0', borderColor: '#D4D4D4' }} />
        )}
        {data.PASSPHRASE && (<>
          <Space direction='vertical' size={0} style={{ width: '100%' }}>
            <Space data-testid='passphrase-onboarded-block'>
              <Text strong>
                {$t({defaultMessage: 'Password:'})}
              </Text>
              <PassphraseViewer
                passphrase={data.PASSPHRASE}
                emailNotification={data.COMMUNICATION_AUTO_DELIVERY === 'false'
                  && data.USER_EMAIL_ENABLED === 'true'}
                smsNotification={data.COMMUNICATION_AUTO_DELIVERY === 'false'
                  && data.USER_SMS_ENABLED === 'true'}
              />
            </Space>
            {data.SAE_PASSPHRASE &&
              <Text style={{fontSize: '12px', lineHeight: '16px', color: '#565758'}}>
                {$t({defaultMessage: 'For older devices that don’t support WPA3'})}
              </Text>
            }
          </Space>
          <Space direction='vertical' align='center' style={{width: '100%'}}>
            <Text>
              {$t({defaultMessage: 'Scan this QR code to connect to the network:'})}
            </Text>
            <QRCodeSVG
              data-testid='qr-code'
              value={standardQRCode}
              size={180}
              bgColor={'#ffffff'}
              fgColor={'#000000'}
              level={'L'}
              includeMargin={false}
            />
          </Space>
        </>)}
      </Space>
  )
}

